import { useEffect, useMemo, useState } from "react";



import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";


export type ExportScopeMode = "all" | "limit";

export interface ExportScopeTableItem {
  tableId: string;
  title: string;
  headers: string[];
}

export interface ExportScopeResult {
  mode: ExportScopeMode;
  rowCount?: number;
  selectedColumnsByTable: Record<string, string[]>;
}

interface TableExportScopeModalProps {
  isOpen: boolean;
  isLoading: boolean;
  contextTitle: string;
  tables: ExportScopeTableItem[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (result: ExportScopeResult) => void | Promise<void>;
}

const TableExportScopeModal: React.FC<TableExportScopeModalProps> = ({
  isOpen,
  isLoading,
  contextTitle,
  tables,
  onOpenChange,
  onSubmit
}) => {
  const [mode, setMode] = useState<ExportScopeMode>("all");
  const [rowCount, setRowCount] = useState("100");
  const [selectedColumnsByTable, setSelectedColumnsByTable] = useState<
    Record<string, string[]>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalColumns = useMemo(
    () => tables.reduce((sum, table) => sum + table.headers.length, 0),
    [tables]
  );

  useEffect(() => {
    if (!isOpen) return;

    const defaults: Record<string, string[]> = {};
    tables.forEach((table) => {
      defaults[table.tableId] = [...table.headers];
    });

    setSelectedColumnsByTable(defaults);
    setMode("all");
    setRowCount("100");
  }, [isOpen, tables]);

  const toggleColumn = (tableId: string, column: string) => {
    setSelectedColumnsByTable((prev) => {
      const current = prev[tableId] ?? [];
      const next = current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column];

      return {
        ...prev,
        [tableId]: next
      };
    });
  };

  const setAllColumnsForTable = (tableId: string, headers: string[]) => {
    setSelectedColumnsByTable((prev) => ({
      ...prev,
      [tableId]: [...headers]
    }));
  };

  const clearColumnsForTable = (tableId: string) => {
    setSelectedColumnsByTable((prev) => ({
      ...prev,
      [tableId]: []
    }));
  };

  const handleSubmit = async () => {
    const hasAnyColumn = tables.some(
      (table) => (selectedColumnsByTable[table.tableId] ?? []).length > 0
    );
    if (!hasAnyColumn || isLoading || isSubmitting) return;

    const parsedRowCount = Number(rowCount);
    const normalizedRowCount =
      mode === "limit" && Number.isFinite(parsedRowCount) && parsedRowCount > 0
        ? Math.floor(parsedRowCount)
        : undefined;

    setIsSubmitting(true);
    onOpenChange(false);

    try {
      await onSubmit({
        mode,
        rowCount: normalizedRowCount,
        selectedColumnsByTable
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden p-0">
        <div className="flex h-full flex-col overflow-hidden bg-white">
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6">
            <DialogHeader className="text-right">
              <DialogTitle className="text-xl font-bold text-slate-800">
                {contextTitle}
              </DialogTitle>

              <DialogDescription className="text-sm text-slate-600">
                ستون های خروجی یا اشتراک را انتخاب کنید و محدوده تعداد ردیف را
                مشخص کنید.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-5">
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="mb-3 text-right text-sm font-semibold text-slate-700">
                  محدوده ردیف ها
                </div>

                <div className="flex flex-wrap items-center justify-end gap-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <span>تمام دیتا</span>

                    <Checkbox
                      checked={mode === "all"}
                      onCheckedChange={() => setMode("all")}
                    />
                  </label>

                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <span>تعداد مشخص</span>

                    <Checkbox
                      checked={mode === "limit"}
                      onCheckedChange={() => setMode("limit")}
                    />
                  </label>

                  <Input
                    type="number"
                    min={1}
                    value={rowCount}
                    onChange={(event) => setRowCount(event.target.value)}
                    className="w-32 text-right"
                    disabled={mode !== "limit"}
                    placeholder="تعداد"
                  />
                </div>
              </div>

              <div className="text-right text-xs text-slate-500">
                {tables.length} جدول و {totalColumns} ستون قابل انتخاب است.
              </div>

              <div className="space-y-4">
                {tables.map((table) => {
                  const selectedColumns =
                    selectedColumnsByTable[table.tableId] ?? [];

                  return (
                    <div
                      key={table.tableId}
                      className="rounded-lg border border-slate-200 p-3"
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => clearColumnsForTable(table.tableId)}
                            disabled={isLoading}
                          >
                            حذف همه
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setAllColumnsForTable(
                                table.tableId,
                                table.headers
                              )
                            }
                            disabled={isLoading}
                          >
                            انتخاب همه
                          </Button>
                        </div>

                        <div className="text-right">
                          <div className="text-sm font-semibold text-slate-800">
                            {table.title}
                          </div>

                          <div className="text-xs text-slate-500">
                            {selectedColumns.length} ستون انتخاب شده
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        {table.headers.map((header) => {
                          const checked = selectedColumns.includes(header);

                          return (
                            <label
                              key={`${table.tableId}-${header}`}
                              className="flex cursor-pointer items-center justify-between rounded-md border border-slate-200 px-3 py-2"
                            >
                              <span className="text-sm text-slate-700">
                                {header}
                              </span>

                              <Checkbox
                                checked={checked}
                                onCheckedChange={() =>
                                  toggleColumn(table.tableId, header)
                                }
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              انصراف
            </Button>

            <Button onClick={handleSubmit} disabled={isLoading || isSubmitting}>
              تایید
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TableExportScopeModal;